import React, { useState, useRef, useEffect } from 'react';
import { Product, ChatMessage }  from '../types';
import { sendChatMessage }       from '../utils/api';
import { WHATSAPP_URL, CHAT_SUGGESTIONS } from '../utils/constants';
import './AIChatbot.css';

interface AIChatbotProps {
  productContext?: Partial<Product>;
}

const INITIAL_MESSAGE: ChatMessage = {
  role:    'assistant',
  content: '🙏 Namaste! I\'m your Balaji Fashion House assistant.\n\nAsk me about:\n• Our latest collections\n• Size guidance\n• Styling tips\n• Product enquiries',
};

const AIChatbot: React.FC<AIChatbotProps> = ({ productContext }) => {
  const [open,     setOpen]     = useState<boolean>(false);
  const [messages, setMessages] = useState<ChatMessage[]>([INITIAL_MESSAGE]);
  const [input,    setInput]    = useState<string>('');
  const [loading,  setLoading]  = useState<boolean>(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const send = async (): Promise<void> => {
    if (!input.trim() || loading) return;

    const userMsg: ChatMessage = { role: 'user', content: input.trim() };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');
    setLoading(true);

    try {
      const data = await sendChatMessage(updated.slice(-10), productContext);
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: `Sorry, I'm having trouble. Reach us on WhatsApp: ${WHATSAPP_URL()}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') send();
  };

  const handleSuggestion = (s: string): void => setInput(s);

  return (
    <>
      <button className={`chat-fab ${open ? 'open' : ''}`} onClick={() => setOpen(o => !o)}>
        {open ? '✕' : '🤖'}
        {!open && <span className="fab-label">AI Chat</span>}
      </button>

      {open && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="chat-avatar"><img src="/logo.png" alt="Balaji Fashion House" /></div>
            <div>
              <div className="chat-title">Balaji Fashion AI</div>
              <div className="chat-status">● Online</div>
            </div>
            <a href={WHATSAPP_URL()} target="_blank" rel="noreferrer"
               className="chat-whatsapp-btn" title="Chat on WhatsApp">💬</a>
          </div>

          <div className="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`msg ${m.role}`}>
                {m.role === 'assistant' && <div className="msg-avatar"><img src="/logo.png" alt="Balaji Fashion House" /></div>}
                <div className="msg-bubble" style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>
              </div>
            ))}
            {loading && (
              <div className="msg assistant">
                <div className="msg-avatar"><img src="/logo.png" alt="Balaji Fashion House" /></div>
                <div className="msg-bubble typing"><span /><span /><span /></div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="quick-replies">
            {CHAT_SUGGESTIONS.map(s => (
              <button key={s} onClick={() => handleSuggestion(s)} className="quick-reply">{s}</button>
            ))}
          </div>

          <div className="chat-input">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about products, sizes, styling..."
              disabled={loading}
            />
            <button onClick={send} disabled={loading || !input.trim()} className="send-btn">
              {loading ? '⏳' : '➤'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatbot;
